import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, from } from 'rxjs';
import {
  ClientRegisteredVendor,
  ExemptVendor,
  Vendor
} from 'src/app/classes/models';
import { ConfirmArchiveVendorModalComponent } from 'src/app/modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-admin-vendor-admin',
  templateUrl: './admin-vendor-admin.component.html',
  styleUrls: ['./admin-vendor-admin.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorAdminComponent {
  constructor(
    private SharedDataService: SharedDataService,
    private VendorService: VendorService,
    private ExemptVendorService: ExemptVendorService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private router: Router,
    private UtilityService: UtilityService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            this.VendorService.getVendorV2(this.VendorId, {
              attributes: JSON.stringify([
                'id',
                'legalName',
                'allowDuplicateTaxId'
              ])
            }).subscribe({
              next: (Vendor) => {
                const { legalName } = Vendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor legal name.',
                    { returnError: true }
                  );
                }

                Object.assign(this, { Vendor });

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Admin - ${legalName}`
                );

                this.ChangeDetectorRef.markForCheck();
              }
            });
          }

          if (eventName === 'SetExemptVendorId' && typeof data === 'number') {
            this.ExemptVendorId = data;

            const vendorQuery = {
              id: data,
              Company: {
                attributes: ['id']
              }
            };

            this.ExemptVendorService.getExemptVendor(
              this.UtilityService.stringifyObjectsInQueryObject(vendorQuery)
            ).subscribe({
              next: (ExemptVendor: ExemptVendor) => {
                const { legalName, Companies } = ExemptVendor;

                if (!Companies || Companies.length === 0) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor Company.',
                    { returnError: true }
                  );
                }

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor legal name.',
                    { returnError: true }
                  );
                }

                const company = Companies[0];
                const { id } = company;

                Object.assign(this, { ExemptVendor, CompanyId: id });

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Admin - ${legalName}`
                );

                this.ChangeDetectorRef.markForCheck();
              }
            });
          }

          if (
            eventName === 'SetClientRegisteredVendorId' &&
            typeof data === 'number'
          ) {
            this.ClientRegisteredVendorId = data;

            const vendorQuery = {
              id: data,
              CompanyVendor: {
                attributes: ['id', 'CompanyComplianceTypeId']
              }
            };

            this.ClientRegisteredVendorService.getClientRegisteredVendor(
              this.UtilityService.stringifyObjectsInQueryObject(vendorQuery)
            ).subscribe({
              next: (ClientRegisteredVendor: ClientRegisteredVendor) => {
                const { CompanyId, legalName, CompanyVendors } =
                  ClientRegisteredVendor;

                if (!CompanyVendors || CompanyVendors.length === 0) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor CompanyVendors.',
                    { returnError: true }
                  );
                }

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor legal name.',
                    { returnError: true }
                  );
                }

                const companyVendor = CompanyVendors[0];
                const { CompanyComplianceTypeId } = companyVendor;

                Object.assign(ClientRegisteredVendor, {
                  CompanyComplianceTypeId
                });
                Object.assign(this, { ClientRegisteredVendor, CompanyId });

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Admin - ${legalName}`
                );

                this.ChangeDetectorRef.markForCheck();
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  CompanyId?: number;

  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;
  SharedDataServiceSubscription?: Subscription;

  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;

  duplicateTaxIdToggle() {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    Object.assign(this.Vendor, {
      allowDuplicateTaxId: !this.Vendor.allowDuplicateTaxId
    });

    const { id: VendorId, allowDuplicateTaxId } = this.Vendor;

    this.VendorService.saveVendor({
      id: VendorId,
      allowDuplicateTaxId
    }).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          `Duplicate Tax ID is ${allowDuplicateTaxId ? 'allowed' : 'disallowed'} for this Vendor.`
        );
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  archiveVendor() {
    if (!this.Vendor && !this.ExemptVendor && !this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmArchiveVendorModalComponent,
      { backdrop: 'static', size: 'lg', centered: true }
    );
    Object.assign(componentInstance, { modelType: 'vendor' });

    const navigationRoute = ['admin', 'vendors'];

    if (this.Vendor) {
      Object.assign(this.Vendor, { isArchived: !this.Vendor.isArchived });

      const { id: VendorId, isArchived } = this.Vendor;

      from(result).subscribe({
        next: (note) => {
          if (note) {
            this.VendorService.saveVendor({
              id: VendorId,
              isArchived,
              Notes: [note]
            }).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully archived Vendor.'
                );
                this.router.navigate(navigationRoute);
              }
            });
          }
        }
      });
    }

    if (this.ExemptVendor) {
      Object.assign(this.ExemptVendor, {
        isArchived: !this.ExemptVendor.isArchived
      });

      const { id: ExemptVendorId, isArchived } = this.ExemptVendor;

      from(result).subscribe({
        next: (note) => {
          if (note) {
            this.ExemptVendorService.saveExemptVendor({
              id: ExemptVendorId,
              isArchived,
              CompanyId: this.CompanyId,
              Notes: [note]
            }).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully archived Vendor.'
                );
                this.router.navigate(navigationRoute);
              }
            });
          }
        }
      });
    }

    if (this.ClientRegisteredVendor) {
      Object.assign(this.ClientRegisteredVendor, {
        isArchived: !this.ClientRegisteredVendor.isArchived
      });

      from(result).subscribe({
        next: (note) => {
          if (note) {
            this.ClientRegisteredVendorService.saveClientRegisteredVendor(
              Object.assign(this.ClientRegisteredVendor!, { Notes: [note] })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully archived Vendor.'
                );
                this.router.navigate(navigationRoute);
              }
            });
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }
}
